import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'

/**
 * Bitway Earn intrinsic APR — read straight off each staking vault's
 * `getCurrentRewardRate(token)` (see BITWAY.md at the repo root).
 *
 * Bitway settles its off-chain (CEX custody) strategy yield into the
 * vault at a FIXED admin-set rate, and that dial IS the user rate: the
 * getter returns percent×100 (750 = 7.5%), and the exchange rate accrues
 * at it linearly — validated 2026-08-19 by an 18-day archival delta on
 * Core Alpha USDT (7.92% measured vs the 8% dial). No API exists;
 * DefiLlama's `bitway-earn` adapter reads the SAME getter (a mirror, not
 * independent validation) and misses the largest pool (Absolute Return
 * U, $39.4M), so it is deliberately not wired as a fallback.
 *
 * The dial moves: it stepped 12% → 8% over five months on Core Alpha
 * USDT. It is re-read on every fetch; nothing here caches.
 */

const REWARD_RATE_ABI = [
  {
    name: 'getCurrentRewardRate',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ type: 'address', name: 'token' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/** One leg: a staking vault × supported token, keyed by the LP token. */
const BITWAY_LEGS: { share: Address; vault: Address; token: Address }[] = [
  // Absolute Return 0x5C4a6903…
  {
    share: '0xccafb706225331aedfec75b5347d462b98ed2fd2', // bwUSDT
    vault: '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    token: '0x55d398326f99059ff775485246999027b3197955', // USDT
  },
  {
    share: '0xaa3d2534b4b87a2859e28c223f18265244ffffb7', // bwU
    vault: '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    token: '0xce24439f2d9c6a2289f741120fe202248b666666', // U
  },
  {
    share: '0x82356c921422a2202e0f96dfccc352598ca8ef71', // bwBTW
    vault: '0x5c4a6903732532eeb3ae0803e062d8ae25d52bd1',
    token: '0x444045b0ee1ee319a660a5e3d604ca0ffa35acaa', // BTW
  },
  // Core Alpha 0xb82E3206…
  {
    share: '0x73af543d809c8d3414e5b92b3aa2c25b182ba3a1', // BTWUSDT
    vault: '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    token: '0x55d398326f99059ff775485246999027b3197955', // USDT
  },
  {
    share: '0xb5c3617d4f077851cc6c7fae558d32e9782307f9', // BTWUSD1
    vault: '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    token: '0x8d0d000ee44948fc98c9b98a4fa4921476f08b0d', // USD1
  },
  {
    share: '0x4effb6bce5cad64d7162c7f7f15f557221b106d5', // BTWU
    vault: '0xb82e32062c773c7748776c06fdb11b92edae3b63',
    token: '0xce24439f2d9c6a2289f741120fe202248b666666', // U
  },
]

/** Result key for one Bitway leg — keyed by the LP (share) token, since
 *  two vaults serve the same underlying on the same chain. */
export const bitwayYieldKey = (share: string): string =>
  `bitway::${Chain.BNB_SMART_CHAIN_MAINNET}::${share.toLowerCase()}`

export const bitwayFetcher: YieldFetcher = {
  label: 'BITWAY_EARN',
  fetch: async () => {
    const rates = (await multicallRetryUniversal({
      chain: Chain.BNB_SMART_CHAIN_MAINNET,
      abi: REWARD_RATE_ABI,
      calls: BITWAY_LEGS.map(({ vault, token }) => ({
        address: vault,
        name: 'getCurrentRewardRate',
        params: [token],
      })),
      allowFailure: true,
    })) as (bigint | undefined | null)[]

    const out: Record<string, number> = {}
    BITWAY_LEGS.forEach(({ share }, i) => {
      const raw = rates[i]
      if (typeof raw !== 'bigint') return
      const apr = Number(raw) / 100
      // The dial is percent×100 (500–800 observed). A four-digit-percent
      // reading means the scale changed — drop it rather than publish it.
      if (Number.isFinite(apr) && apr >= 0 && apr <= 1000) {
        out[bitwayYieldKey(share)] = apr
      }
    })
    if (Object.keys(out).length === 0) {
      throw new Error('Bitway: no leg returned a readable reward rate')
    }
    return out
  },
}
